import { CoverImageFragment } from "@src/fragments.generated";
import CoverImagePicker from "@src/features/contentDetail/ui/CoverImagePicker";
import PinturaModal from "@src/lib/ui/PinturaModal";
import Spacer from "@src/lib/ui/Spacer";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import MenuIcon from "@src/lib/icons/18px/menu";
import ClipboardIcon from "@src/lib/icons/18px/clipboard";
import AsteriskIcon from "@src/lib/icons/18px/asterisk";
import BookmarkOutlineIcon from "@src/lib/icons/18px/bookmark-outline";
import BucketPaintOutlineIcon from "@src/lib/icons/18px/bucket-paint-outline";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import Text from "@src/lib/ui/Text";
import useHover from "@src/lib/hooks/useHover";
import { useToast } from "@src/lib/hooks/useToast";
import * as Popover from "@radix-ui/react-popover";
import { BackgroundGradient, CoverImageInput } from "@src/types.generated";
import { getCoverImageUrl } from "@src/features/contentDetail/utils/media";
import { cn } from "@src/lib/utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import { CSSProperties, FC, useMemo, useState } from "react";

type Props = {
  coverImage?: CoverImageFragment;
  isPath?: boolean;
  isSkill?: boolean;
  isTaskList?: boolean;
  iconSize?: number;
  editEnabled: boolean;
  onNewCoverImageSelected?: (
    newCoverImage: Omit<CoverImageInput, "id">,
  ) => void;
  onCoverImageRemoved?: () => void;
  style?: CSSProperties;
  className?: string;
  disableIcon?: boolean;
};

const CoverImage: FC<Props> = ({
  coverImage,
  isPath = false,
  isSkill = false,
  isTaskList = false,
  editEnabled,
  onNewCoverImageSelected,
  onCoverImageRemoved,
  style,
  className,
  iconSize,
  disableIcon,
}) => {
  const { addErrorToast } = useToast();
  const [open, setOpen] = useState(false);
  const [showPinturaModal, setShowPinturaModal] = useState(false);
  const coverImageUrl = useMemo(
    () => (coverImage ? getCoverImageUrl(coverImage) : null),
    [coverImage],
  );
  const fullSizeUrl = useMemo(
    () =>
      coverImage?.imageUrls?.medium ||
      coverImage?.imageUrls?.original ||
      coverImage?.imageUrls?.wide,
    [coverImage],
  );
  const displayIcon = useMemo(
    () => coverImageUrl == null && !disableIcon,
    [coverImageUrl, disableIcon],
  );
  const fallbackBackgroundStyle = useMemo(() => {
    if (coverImageUrl || !coverImage?.background) return undefined;
    const value = LINEAR_GRADIENTS[coverImage.background];
    if (
      value.startsWith("linear-gradient") ||
      value.startsWith("radial-gradient")
    ) {
      return { backgroundImage: value };
    }
    return { backgroundColor: value };
  }, [coverImage?.background, coverImageUrl]);
  const [hover, hoverBind] = useHover();
  const contentIcon = useMemo(() => {
    const size = iconSize || 64;
    const cls = `text-white`;
    if (isPath) {
      return <MenuIcon className={cls} width={size} height={size} />;
    }
    if (isTaskList) {
      return <ClipboardIcon className={cls} width={size} height={size} />;
    }
    if (isSkill) {
      return <AsteriskIcon className={cls} width={size} height={size} />;
    }
    return <BookmarkOutlineIcon className={cls} width={size} height={size} />;
  }, [isPath, isTaskList, isSkill, iconSize]);
  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center overflow-hidden rounded-2xl bg-gray-5-alpha",
        className,
      )}
      style={{
        ...fallbackBackgroundStyle,
        ...style,
      }}
      {...hoverBind}
    >
      {coverImageUrl ? (
        <img
          src={coverImageUrl}
          alt=""
          className="pointer-events-none absolute inset-0 size-full object-cover object-center"
        />
      ) : null}
      {editEnabled && fullSizeUrl ? (
        <>
          <PinturaModal
            aspectRatio={16 / 9}
            onNewImageUrl={(imageUrl) => {
              onNewCoverImageSelected
                ? onNewCoverImageSelected({
                    mediaUrl: imageUrl,
                    emojiIcon: null,
                  })
                : addErrorToast({ message: "Unable to update cover image" });
            }}
            imageUrl={fullSizeUrl}
            visible={showPinturaModal}
            hide={() => setShowPinturaModal(false)}
          />
          <div
            className="absolute right-3 bottom-[50px] flex cursor-pointer items-center rounded-2xl bg-black py-1 pr-3 pl-2 transition-opacity duration-200 ease-in-out"
            style={{
              opacity: hover ? 0.7 : 0,
            }}
            onClick={() => setShowPinturaModal(true)}
          >
            <PenOutlineIcon className="text-white" />
            <Spacer size={4} axis={"horizontal"} />
            <Text
              variant="fine-print-title"
              className="text-white"
              style={{
                textTransform: "uppercase",
              }}
            >
              Edit Image
            </Text>
          </div>
        </>
      ) : null}
      {coverImage?.emojiIcon && (
        <div
          style={{
            fontSize: iconSize || 64,
            textAlign: "center",
          }}
        >
          {coverImage.emojiIcon}
        </div>
      )}
      {!coverImage?.emojiIcon && displayIcon && contentIcon}
      {editEnabled && (
        <>
          {onCoverImageRemoved && coverImage?.imageUrls && (
            <div
              className="absolute right-3 bottom-[88px] flex cursor-pointer items-center rounded-2xl bg-black py-1 pr-3 pl-2 transition-opacity duration-200 ease-in-out"
              style={{
                opacity: hover ? 0.7 : 0,
              }}
              onClick={onCoverImageRemoved}
            >
              <TrashOutlineIcon className="text-white" />
              <Spacer size={4} axis={"horizontal"} />
              <Text
                variant="fine-print-title"
                className="text-white"
                style={{
                  textTransform: "uppercase",
                }}
              >
                Remove image
              </Text>
            </div>
          )}
          <Popover.Root open={open} onOpenChange={(open) => setOpen(open)}>
            <Popover.Trigger asChild>
              <Popover.Anchor>
                <div
                  className="absolute right-3 bottom-3 flex cursor-pointer items-center rounded-2xl bg-black py-1 pr-3 pl-2 transition-opacity duration-200 ease-in-out"
                  style={{
                    opacity: hover ? 0.7 : 0,
                  }}
                  onClick={() => setOpen(true)}
                >
                  <BucketPaintOutlineIcon className="size-4 text-white" />
                  <Spacer size={4} axis={"horizontal"} />
                  <Text
                    variant="fine-print-title"
                    className="text-white"
                    style={{
                      textTransform: "uppercase",
                    }}
                  >
                    Change Cover
                  </Text>
                </div>
              </Popover.Anchor>
            </Popover.Trigger>
            <Popover.Portal>
              <AnimatePresence>
                <Popover.Content
                  asChild
                  sideOffset={40}
                  align="center"
                  className="relative z-[9999] h-full w-[400px] overflow-hidden rounded-2xl bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)]"
                >
                  <motion.div
                    initial={{ scaleX: 1, scaleY: 0.6, opacity: 0 }}
                    animate={{
                      scaleY: 1,
                      opacity: 1,
                      transition: { type: "spring", duration: 0.2 },
                    }}
                    style={{ originY: 0, originX: 1 }}
                  >
                    <CoverImagePicker
                      coverImage={coverImage}
                      onCoverImageSelected={onNewCoverImageSelected}
                    />
                  </motion.div>
                </Popover.Content>
              </AnimatePresence>
            </Popover.Portal>
          </Popover.Root>
        </>
      )}
    </div>
  );
};

export default CoverImage;

export const LINEAR_GRADIENTS: Record<BackgroundGradient, string> = {
  [BackgroundGradient.Gradient_1]:
    "linear-gradient(to bottom right, #05A4FE, #E8BAFB)",
  [BackgroundGradient.Gradient_2]:
    "linear-gradient(to bottom right, #FFBBBA, #E8BAFB)",
  [BackgroundGradient.Gradient_3]:
    "linear-gradient(to bottom right, #FFADDE, #ADBFFD)",
  [BackgroundGradient.Gradient_4]:
    "linear-gradient(to bottom right, #E8BAFB, #EFF9DF)",
  [BackgroundGradient.Gradient_5]:
    "linear-gradient(to bottom right, #E8BAFB, #92EAEB)",
  [BackgroundGradient.Gradient_6]:
    "linear-gradient(to bottom right, #D6F7DB, #8EE8F7)",
  [BackgroundGradient.Gradient_7]:
    "linear-gradient(to bottom right, #92EAEB, #FFE375)",
  [BackgroundGradient.Gradient_8]:
    "linear-gradient(to bottom right, #FAD4CD, #FEABC4)",
  [BackgroundGradient.Gradient_9]:
    "linear-gradient(to bottom right, #FFE375, #FFBBBA)",
  [BackgroundGradient.Gradient_10]:
    "linear-gradient(to bottom right, #FFC48C, #FFADDE)",

  [BackgroundGradient.Blue]: "#93aff6",
  [BackgroundGradient.Red]: "#f5b2ad",
  [BackgroundGradient.Orange]: "#f9c594",
  [BackgroundGradient.Purple]: "#cdaef4",
  [BackgroundGradient.Green]: "#8ce8b6",
  [BackgroundGradient.Yellow]: "#f9e597",
  [BackgroundGradient.Gray]: "#babdc5",
};
